import { useState, useCallback } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { InquiryDraftFormData } from '@/hooks/useInquiryForm';
import { segmentClient } from '@/util/analytics';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';
import { isAskAnonymously, isPrimumSupport } from '@/util/userFilters';
import { isMedicalOnDemandExpert } from '@/utils/expertHelpers';

interface UseExpertSelectionFieldProps {
  control: Control<InquiryDraftFormData>;
  setValue: UseFormSetValue<InquiryDraftFormData>;
  cancerFocusId: string;
  filteredExperts: ExpertProfile[];
  currentUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  analyticsSource: 'inquiry' | 'directory';
  maxItems: number;
  isDisabled: boolean;
  isLoadingFilteredExperts: boolean;
  isFetchingFilteredExperts?: boolean;
  isDraft?: boolean;
}

/**
 * Hook for managing expert selection field state and handlers
 *
 * Encapsulates common logic for expert selection across both inquiry forms:
 * - Toggle enableExpertSelection checkbox
 * - Handle modal open/close
 * - Track analytics events
 * - Clear expertIds when unchecked
 * - Handle removing final expert
 *
 * **Checkbox Enabled State:**
 * - Drafts: Enabled immediately if cancer type is selected (even while experts load)
 * - New inquiries: Enabled only after experts finish loading and are available
 * - Stays enabled during refetches (isFetching && !isLoading) to prevent UI flicker
 */
export function useExpertSelectionField({
  control,
  setValue,
  cancerFocusId,
  filteredExperts,
  currentUser,
  analyticsSource,
  maxItems,
  isDisabled,
  isLoadingFilteredExperts,
  isFetchingFilteredExperts = false,
  isDraft = false,
}: UseExpertSelectionFieldProps) {
  const [isExpertMultiSelectOpen, setIsExpertMultiSelectOpen] = useState(false);

  const realExperts = filteredExperts.filter(
    (expert) =>
      !isAskAnonymously(expert.name) &&
      !isPrimumSupport(expert.name) &&
      !isMedicalOnDemandExpert(expert)
  );

  const hasExperts = realExperts.length > 0;

  /**
   * Checkbox is enabled when we have usable expert data available.
   * - Drafts: enable while loading to prevent flicker, but disable if loading completes with no experts
   * - Refetches: keep enabled during refetch (isFetching && !isLoading) to prevent flicker
   * - Initial load: enable only after loading complete and experts available
   */
  const isRefetching = isFetchingFilteredExperts && !isLoadingFilteredExperts;

  const isDraftReady = isDraft && cancerFocusId && (isLoadingFilteredExperts || hasExperts);

  const hasLoadedExperts = !isLoadingFilteredExperts && hasExperts;

  const hasUsableExpertData = isDraftReady || isRefetching || hasLoadedExperts;

  const isCheckboxDisabled = isDisabled || !hasUsableExpertData;

  const handleSendToAdditionalChange = useCallback(
    (checked: boolean) => {
      setValue('enableExpertSelection', checked);

      const userName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();

      segmentClient.track(ANALYTICS_EVENTS.EXPERT_SELECTION_TOGGLED, {
        enabled: checked,
        source: analyticsSource,
        cancerFocusId,
        availableExpertsCount: realExperts.length,
        userName,
      });

      if (checked) {
        segmentClient.track(ANALYTICS_EVENTS.ADDITIONAL_EXPERTS_ENABLED, {
          cancerFocusId,
          availableExpertsCount: realExperts.length,
          userName,
        });

        setIsExpertMultiSelectOpen(true);
      }

      if (!checked) {
        setValue('expertIds', []);

        setIsExpertMultiSelectOpen(false);
      }
    },
    [setValue, currentUser, analyticsSource, cancerFocusId, realExperts.length]
  );

  const handleMultiSelectClose = useCallback(
    (expertIds: number[]) => {
      setIsExpertMultiSelectOpen(false);

      if (expertIds.length === 0) {
        setValue('enableExpertSelection', false);
      } else {
        setValue('enableExpertSelection', true);
      }
    },
    [setValue]
  );

  const handleRemoveSelectedItem = useCallback(
    (expertId: string, currentExpertIds: number[]) => {
      const isRemovingFinalExpert =
        currentExpertIds.length === 1 && currentExpertIds[0] === Number(expertId);

      if (isRemovingFinalExpert) {
        setValue('enableExpertSelection', false);
      }
    },
    [setValue]
  );

  return {
    isExpertMultiSelectOpen,
    setIsExpertMultiSelectOpen,
    realExperts,
    hasExperts,
    isCheckboxDisabled,
    handleSendToAdditionalChange,
    handleMultiSelectClose,
    handleRemoveSelectedItem,
  };
}
